import { usePagination } from '@lfg/core/hooks';
import { Add as AddIcon, Clear as ClearIcon } from '@mui/icons-material';
import { Box, Paper, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { parse } from 'papaparse';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';

import { AppPagination } from 'src/components/Pagination';
import { Show } from 'src/components/Show';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs, ProjectKolDTO } from 'src/global/types/project.type';
import { parseConfig, validateCsv } from 'src/utils/template';

import { AdminNumberFormat } from '../AdminNumberFormat';
import { AdminInput } from '../AdminTextField';
import { DownloadButton } from '../DownloadButton';
import { CustomTableBodyCell, CustomTableHeadCell } from '../FundingDistribution/CustomTableCell';
import { UploadButton } from '../UploadButton';

export function KolTable({ isDisabled }: { isDisabled?: boolean }) {
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'kols',
  });
  const { data, page, totalPages, volume, setPage, setVolume } = usePagination(fields);

  const handleChangePage = (_event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setVolume(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleUploadCsvFile = (files: FileList) => {
    const file = files[0];
    if (!file) {
      toast.error('No file selected');
      return;
    }
    const reader = new FileReader();

    reader.onload = async e => {
      try {
        if (!e.target?.result) {
          toast.error('Error reading file');
          return;
        }

        const csv = e.target.result as string;
        const validationRes = validateCsv(csv, {
          headers: ['ADDRESS', 'discountPercentage'],
          columnTypes: ['address', 'number'],
        });

        if (!validationRes.isValid) {
          toast.error(validationRes.errors[0]);
          return;
        }

        const parseResult = parse(csv, parseConfig);
        const csvKols = parseResult.data
          .slice(1)
          .filter((row: any) => row.length >= 2 && row[0]?.trim() && !isNaN(Number(row[1])))
          .map((row: any) => ({
            address: row[0].trim(),
            discount: Number(row[1]),
          }))
          .filter(
            kol => kol.address && kol.discount >= 0 && kol.discount <= 100, // Assuming discount should be between 0-100%
          );

        if (csvKols.length === 0) {
          toast.error('No valid KOL data found in CSV');
          return;
        }

        // Get the current KOL data with their IDs
        const currentKols = [...fields];

        // Create a new array to hold the updated KOLs
        const updatedKols = [...currentKols];

        let updatedCount = 0;
        let addedCount = 0;

        // Process CSV KOLs
        csvKols.forEach(csvKol => {
          // Check if a KOL with the same address already exists
          const existingIndex = currentKols.findIndex(
            existing => existing.address.toLowerCase() === csvKol.address.toLowerCase(),
          );

          if (existingIndex >= 0) {
            // Update the existing KOL's discount
            updatedKols[existingIndex] = {
              ...updatedKols[existingIndex], // Preserve the id and other properties
              discount: csvKol.discount,
            };
            updatedCount++;
          } else {
            // This is a new KOL
            addedCount++;
          }
        });

        // First replace with updated existing KOLs
        replace(updatedKols);

        // Then find and append new KOLs
        const newKols = csvKols.filter(
          csvKol => !currentKols.some(existing => existing.address.toLowerCase() === csvKol.address.toLowerCase()),
        );

        // Append each new KOL (this will add the id automatically)
        if (newKols.length > 0) {
          append(newKols as ProjectKolDTO[]);
        }

        // Reset to first page after import
        setPage(0);

        toast.success(`KOL data updated successfully (${addedCount} added, ${updatedCount} updated)`);
      } catch (error) {
        console.error('Error processing CSV:', error);
        toast.error('Error processing CSV file');
      }
    };

    reader.onerror = () => {
      toast.error('Error reading the CSV file');
    };

    reader.readAsText(file);
  };

  return (
    <Box>
      <Show when={!isDisabled}>
        <Stack
          spacing={2}
          gap={2}
          marginBottom={3}
          flexDirection={{ xs: 'column', sm: 'row' }}
          style={{
            width: 'fit-content',
          }}>
          <UploadButton accept='.csv' label='Import CSV File' multiple={false} uploadCallback={handleUploadCsvFile} />
          <DownloadButton label='Download Template' fileUrl='/resources/files/templates/kol-template.csv' />
        </Stack>
      </Show>
      <TableContainer
        component={Paper}
        sx={{ minWidth: 650, backgroundColor: 'transparent !important', color: colors.neutral90 }}>
        <Table
          sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90 }}
          size='small'
          aria-label='a dense table'>
          <TableHead sx={{ minWidth: 650, backgroundColor: colors.neutral06, color: colors.neutral90, paddingY: 1 }}>
            <TableRow>
              <CustomTableHeadCell width={'50%'}>Wallet Address</CustomTableHeadCell>
              <CustomTableHeadCell width={'50%'}>Discount percentage</CustomTableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              background: 'transparent',
              color: 'white',
            }}>
            {data.map((field, index) => (
              <TableRow
                key={field.id}
                sx={{
                  paddingY: 1,
                  borderBottom: `1px solid ${index === fields.length - 1 ? 'none' : colors.neutral015}`,
                  '&:last-child td, &:last-child th': { border: 0 },
                }}>
                <CustomTableBodyCell>
                  <Controller
                    name={`kols.${index}.address`}
                    control={control}
                    render={({ field }) => (
                      <AdminInput
                        isError={!!errors?.kols?.[index]?.address}
                        errorMsg={errors?.kols?.[index]?.address?.message}
                        placeholder={'Enter Wallet Address'}
                        focusedBorderColor='white'
                        borderColor={
                          errors?.kols?.[index]?.address ? colors.error : !field.value ? 'white' : 'transparent'
                        }
                        readOnly={isDisabled}
                        {...field}
                      />
                    )}
                  />
                </CustomTableBodyCell>
                <CustomTableBodyCell>
                  <div className='flex justify-between items-start'>
                    <Box width={'-webkit-fill-available'}>
                      <Controller
                        name={`kols.${index}.discount` as any}
                        control={control}
                        render={({ field: { onChange, ...rest } }) => (
                          <AdminNumberFormat
                            onValueChange={values => {
                              onChange(Number(values.floatValue));
                            }}
                            isError={!!errors?.kols?.[index]?.discount}
                            errorMsg={errors?.kols?.[index]?.discount?.message}
                            readOnly={isDisabled}
                            {...rest}
                          />
                        )}
                      />
                    </Box>
                    <Show when={!isDisabled}>
                      <Stack direction='row' spacing={1} alignItems={'center'} marginLeft={'10px'} marginTop={'12px'}>
                        {field.id === fields[fields.length - 1].id && (
                          <AddIcon
                            onClick={() => append({ discount: 0, address: '' })}
                            className='cursor-pointer'
                            sx={{
                              marginLeft: '10px',
                              marginTop: '12px',
                            }}
                          />
                        )}
                        {fields.length > 1 && (
                          <ClearIcon
                            onClick={() => remove(index)}
                            className='cursor-pointer'
                            sx={{
                              marginLeft: '10px',
                              color: colors.error,
                              marginTop: '12px',
                            }}
                          />
                        )}
                      </Stack>
                    </Show>
                  </div>
                </CustomTableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction={'row'} justifyContent={'end'}>
        <AppPagination
          component='div'
          count={fields.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={volume}
          onRowsPerPageChange={handleChangeRowsPerPage}
          nextIconColor={page === totalPages ? colors.neutral015 : 'white'}
          prevIconColor={page === 0 ? colors.neutral015 : 'white'}
        />
      </Stack>
    </Box>
  );
}
